import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { classNames } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { fetchInfoScreenContent, updateInfoScreenContent } from "@/lib/infoScreenApi";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { InfoScreenRotatingDisplay } from "@/pages/info-screen/components/InfoScreenRotatingDisplay";
import { InfoScreenDisplaysPanel } from "@/pages/stammdaten/InfoScreenDisplaysPanel";
import { InfoScreenEventsPanel } from "@/pages/stammdaten/InfoScreenEventsPanel";
import { InfoScreenDesignGallery } from "@/pages/info-screen/components/InfoScreenDesignGallery";
import { getDesignProposal } from "@/pages/info-screen/designs";
import { InfoScreenPageEditor } from "@/pages/stammdaten/InfoScreenPageEditor";
import type { InfoScreenDesign } from "@/types/infoScreen";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import type { InfoScreenContent, InfoScreenPage } from "@/types/infoScreen";
import "@/pages/info-screen/info-screen.css";

function newId(): string {
  return crypto.randomUUID();
}

function emptyPage(name: string, sortOrder: number): InfoScreenPage {
  return {
    id: newId(),
    name,
    enabled: true,
    sort_order: sortOrder,
    layout: "split",
    design: "classic",
    slideshow_interval_seconds: 8,
    images: [],
    text_blocks: [],
    column_left_blocks: [],
    column_right_blocks: [],
  };
}

type InfoScreenTab = "pages" | "events" | "displays";

export function InfoScreenConfigPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: InfoScreenTab =
    tabParam === "displays" ? "displays" : tabParam === "events" ? "events" : "pages";
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteStammdaten(groups);

  const [content, setContent] = useState<InfoScreenContent | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchInfoScreenContent();
      setContent(data);
      const first = data.pages.find((p) => p.enabled) ?? data.pages[0];
      setSelectedPageId((prev) => prev ?? first?.id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageMeta({
      title: "Infobildschirm",
      subtitle:
        tab === "displays"
          ? "Standorte und Geräte-Freischaltung"
          : tab === "events"
            ? "Veranstaltungen in Frankfurt und Umgebung"
            : "Informationsseiten mit Diashow, Texten und Layouts",
    });
  }, [setPageMeta, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedPage = content?.pages.find((p) => p.id === selectedPageId) ?? null;

  const previewContent = useMemo((): InfoScreenContent | null => {
    if (!content || !selectedPage) return content;
    return {
      ...content,
      pages: [selectedPage],
      page_rotation_seconds: 60,
    };
  }, [content, selectedPage]);

  function patchContent(patch: Partial<InfoScreenContent>) {
    if (!content) return;
    setContent({ ...content, ...patch });
    setSuccess(false);
  }

  function updatePage(page: InfoScreenPage) {
    if (!content) return;
    patchContent({
      pages: content.pages.map((p) => (p.id === page.id ? page : p)),
    });
  }

  function addPage() {
    if (!content) return;
    const page = emptyPage("Neue Seite", content.pages.length * 10 + 10);
    patchContent({ pages: [...content.pages, page] });
    setSelectedPageId(page.id);
  }

  function addPageFromDesign(page: InfoScreenPage) {
    if (!content) return;
    const withOrder = {
      ...page,
      sort_order: content.pages.length * 10 + 10,
      name: page.name || "Neue Seite",
    };
    patchContent({ pages: [...content.pages, withOrder] });
    setSelectedPageId(withOrder.id);
  }

  function applyDesignToSelected(designId: InfoScreenDesign) {
    if (!selectedPage || !content) return;
    const proposal = getDesignProposal(designId);
    updatePage({
      ...selectedPage,
      design: designId,
      layout:
        selectedPage.layout === "columns" && proposal.layout !== "columns"
          ? proposal.layout
          : selectedPage.layout,
    });
  }

  function removePage(id: string) {
    if (!content) return;
    const next = content.pages.filter((p) => p.id !== id);
    patchContent({ pages: next });
    if (selectedPageId === id) {
      setSelectedPageId(next[0]?.id ?? null);
    }
  }

  async function onSave() {
    if (!content || !canEdit) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const saved = await updateInfoScreenContent({
        enabled: content.enabled,
        page_rotation_seconds: content.page_rotation_seconds,
        pages: content.pages,
      });
      setContent(saved);
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  if (tab === "displays") {
    return (
      <div className="space-y-4">
        <InfoScreenTabBar tab={tab} onTab={(t) => setSearchParams(t === "pages" ? {} : { tab: t })} />
        <InfoScreenDisplaysPanel />
      </div>
    );
  }

  if (tab === "events") {
    return (
      <div className="space-y-4">
        <InfoScreenTabBar tab={tab} onTab={(t) => setSearchParams(t === "pages" ? {} : { tab: t })} />
        {!canEdit ? <StammdatenWriteHint /> : null}
        <InfoScreenEventsPanel canEdit={canEdit} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <InfoScreenTabBar tab={tab} onTab={(t) => setSearchParams(t === "pages" ? {} : { tab: t })} />
        <p className="text-sm text-label-secondary">Inhalte werden geladen…</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red-600">{error ?? "Inhalte konnten nicht geladen werden."}</p>
        <Button type="button" variant="secondary" size="sm" onClick={() => void load()}>
          Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InfoScreenTabBar tab={tab} onTab={(t) => setSearchParams(t === "pages" ? {} : { tab: t })} />
      {!canEdit ? <StammdatenWriteHint /> : null}

      {previewContent && selectedPage ? (
        <Card title="Vorschau (aktuelle Seite)" bodyClassName="p-0 overflow-hidden rounded-xl">
          <div
            className="guest-tablet-root info-screen-idle-preview"
            data-kiosk="info-screen-preview"
          >
            <div className="info-screen-shell">
              {content.enabled ? (
                <InfoScreenRotatingDisplay content={previewContent} />
              ) : (
                <p className="p-8 text-center text-label-secondary">
                  Infobildschirm ist deaktiviert.
                </p>
              )}
            </div>
          </div>
          <p className="border-t border-separator px-4 py-2 text-xs text-label-secondary">
            <Link to="/info-screen/lobby-1" className="text-accent underline" target="_blank">
              Bildschirm im Browser öffnen
            </Link>
          </p>
        </Card>
      ) : null}

      <Card title="Globale Einstellungen">
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={content.enabled}
              onChange={(e) => patchContent({ enabled: e.target.checked })}
            />
            Infobildschirm aktiv
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Seitenwechsel (Sekunden, wenn mehrere Seiten aktiv)
            <input
              type="number"
              min={5}
              max={300}
              disabled={!canEdit}
              className="eh-input w-28"
              value={content.page_rotation_seconds}
              onChange={(e) =>
                patchContent({
                  page_rotation_seconds: Math.min(
                    300,
                    Math.max(5, Number(e.target.value) || 15),
                  ),
                })
              }
            />
          </label>
        </div>
      </Card>

      <Card title="Informationsseiten">
        <div className="space-y-4">
          <p className="text-sm text-label-secondary">
            Legen Sie beliebig viele Seiten an. Aktive Seiten wechseln automatisch auf dem
            Bildschirm. Wählen Sie ein Design aus der Galerie oder passen Sie Layout, Bilder
            und Texte individuell an.
          </p>

          <InfoScreenDesignGallery
            selectedDesign={selectedPage?.design ?? "classic"}
            canEdit={canEdit}
            onSelectDesign={applyDesignToSelected}
            onCreateFromDesign={addPageFromDesign}
          />

          <div className="flex flex-wrap gap-2">
            {content.pages
              .slice()
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((page) => (
                <button
                  key={page.id}
                  type="button"
                  className={classNames(
                    "rounded-lg border px-3 py-2 text-sm transition-colors",
                    page.id === selectedPageId
                      ? "border-accent bg-accent/10 font-medium text-accent"
                      : "border-separator hover:bg-fill",
                    !page.enabled && "opacity-50",
                  )}
                  onClick={() => setSelectedPageId(page.id)}
                >
                  {page.name}
                  {!page.enabled ? " (inaktiv)" : ""}
                </button>
              ))}
            {canEdit ? (
              <Button type="button" variant="secondary" size="sm" onClick={addPage}>
                Seite hinzufügen
              </Button>
            ) : null}
          </div>

          {selectedPage ? (
            <div className="border-t border-separator pt-4">
              {canEdit ? (
                <div className="mb-4 flex justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      if (window.confirm(`Seite „${selectedPage.name}“ wirklich löschen?`)) {
                        removePage(selectedPage.id);
                      }
                    }}
                  >
                    Seite löschen
                  </Button>
                </div>
              ) : null}
              <InfoScreenPageEditor
                page={selectedPage}
                canEdit={canEdit}
                onChange={updatePage}
              />
            </div>
          ) : (
            <p className="text-sm text-label-tertiary italic">
              Noch keine Seiten – fügen Sie eine Informationsseite hinzu.
            </p>
          )}
        </div>
      </Card>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {success ? <p className="text-sm text-emerald-700">Gespeichert.</p> : null}

      {canEdit ? (
        <div className="flex gap-3">
          <Button type="button" onClick={() => void onSave()} disabled={saving}>
            {saving ? "Speichern…" : "Speichern"}
          </Button>
          <Button type="button" variant="secondary" onClick={() => void load()} disabled={saving}>
            Zurücksetzen
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function InfoScreenTabBar({
  tab,
  onTab,
}: {
  tab: InfoScreenTab;
  onTab: (tab: InfoScreenTab) => void;
}) {
  return (
    <div className="flex gap-1 rounded-xl border border-separator bg-grouped p-1">
      <button
        type="button"
        className={classNames(
          "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          tab === "pages" ? "bg-accent text-label-on-accent shadow-sm" : "text-label-secondary hover:bg-fill",
        )}
        onClick={() => onTab("pages")}
      >
        Seiten & Inhalte
      </button>
      <button
        type="button"
        className={classNames(
          "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          tab === "events"
            ? "bg-accent text-label-on-accent shadow-sm"
            : "text-label-secondary hover:bg-fill",
        )}
        onClick={() => onTab("events")}
      >
        Veranstaltungen
      </button>
      <button
        type="button"
        className={classNames(
          "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          tab === "displays"
            ? "bg-accent text-label-on-accent shadow-sm"
            : "text-label-secondary hover:bg-fill",
        )}
        onClick={() => onTab("displays")}
      >
        Bildschirme / Geräte
      </button>
    </div>
  );
}
